import { IconPolygonArrowLeft } from "@/assets/icons";
import { background, border, icon, text } from "@/theme/colors";
import { Box, ButtonBase, Stack, styled, Typography } from "@mui/material";
import React from "react";

const MIN_COLUMNS = 3;
const MAX_COLUMNS = 7;

export type TProcessedProcedure = {
  id: number;
  stepName: string;
  deadline: React.ReactNode;
  agentLabel: string | null;
  isPast: boolean;
};

export const ExamProcedureList = ({
  procedures,
  selectedProcedureId,
  onSelectProcedure,
}: {
  procedures: TProcessedProcedure[];
  selectedProcedureId: number | null;
  onSelectProcedure: (id: number) => void;
}) => {
  const columns = Math.min(MAX_COLUMNS, Math.max(MIN_COLUMNS, procedures.length));

  return (
    <Box
      display="grid"
      gridTemplateColumns={`repeat(${columns}, minmax(0, 1fr))`}
      alignItems="stretch"
      columnGap={2}
      rowGap={4}
      mt={3}
    >
      {procedures.map((procedure, index) => {
        const isActive = procedure.id === selectedProcedureId;
        return (
          <Stack key={procedure.id} flexDirection="row" alignItems="stretch" gap={2}>
            <Stack flex={1} minWidth={0} justifyContent="flex-end">
              {isActive && procedure.agentLabel && (
                <AgentLabel variant="cap12Bold" color={text.accent_green}>
                  手続きする人: {procedure.agentLabel}
                </AgentLabel>
              )}
              <ExamProcedureItem
                name={procedure.stepName}
                deadline={procedure.deadline}
                active={isActive}
                isPast={procedure.isPast}
                onClick={() => onSelectProcedure(procedure.id)}
              />
            </Stack>
            <Stack
              justifyContent="center"
              flexShrink={0}
              sx={{ opacity: index < procedures.length - 1 ? 1 : 0 }}
              aria-hidden="true"
            >
              <IconPolygonArrowLeft width={11} height={22} color={icon.readonly} />
            </Stack>
          </Stack>
        );
      })}
    </Box>
  );
};

type TExamProcedureItemProps = {
  name: string;
  deadline: React.ReactNode;
  active?: boolean;
  isPast?: boolean;
  onClick?: () => void;
};

const ExamProcedureItem = ({ name, deadline, active, isPast, onClick }: TExamProcedureItemProps) => {
  const borderStyle = active
    ? `2px solid ${border.primary}`
    : isPast
      ? `1px solid ${border.middle}`
      : `2px solid transparent`;

  return (
    <ButtonBase
      aria-label={name}
      onClick={onClick}
      sx={{
        fontFamily: '"Noto Sans JP", sans-serif',
        gap: 1,
        p: 2,
        width: "100%",
        minHeight: 108,
        borderRadius: 2,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "center",
        textAlign: "left",
        border: borderStyle,
        ...(!active && !isPast && { outline: `1px solid ${border.middle}` }),
        "&.Mui-focusVisible": {
          outline: `2px solid ${border.primary}`,
          outlineOffset: 2,
        },
      }}
    >
      <Stack>
        <Typography variant="body14" color={isPast ? text.disable : text.secondary}>
          {name}
        </Typography>
        {deadline}
      </Stack>

      {active && (
        <Stack position="absolute" left="50%" bottom={-14} sx={{ transform: "translateX(-50%)" }} aria-hidden="true">
          <Stack position="relative">
            <IconPolygonArrowLeft
              color={icon.primary}
              width={11}
              height={22}
              style={{ transform: "rotate(90deg)", zIndex: 2 }}
            />
            <IconPolygonArrowLeft
              color={background.white}
              width={11}
              height={22}
              style={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%) rotate(90deg) scale(1.5)",
                zIndex: 1,
              }}
            />
          </Stack>
        </Stack>
      )}
    </ButtonBase>
  );
};

const AgentLabel = styled(Typography)(({ theme }) => ({
  textAlign: "left",
  marginBottom: theme.spacing(0.5),
  wordBreak: "break-word",
}));
